<template>
  <div class="translated-text-v2">
    <div
      v-if="effectiveTranslate"
      class="full-translation-content"
      :class="secondaryTextClass"
    >
      {{ props.translationData.fullTranslation }}
    </div>

    <div v-else class="text-content" :class="primaryTextClass">
      <span
        v-for="(word, wordIndex) in props.translationData.originalWords"
        :key="`word-${wordIndex}`"
        class="word-wrap"
      >
        <span
          class="word"
          :class="{
            'clickable-word': hasTranslation(wordIndex),
            'translated-word': hasTranslation(wordIndex) && !isWordActive(wordIndex),
            'replacement-word': hasTranslation(wordIndex) && isWordActive(wordIndex),
            [getTranslatedWordTextClass()]: hasTranslation(wordIndex) && !isWordActive(wordIndex)
          }"
          :style="getWordStyle(wordIndex)"
          :ref="(el) => setWordRef(wordIndex, el)"
          @click="handleWordClick(wordIndex, $event)"
        >
          {{ getRenderedWord(wordIndex) }}
        </span>
        <span v-if="needsTrailingSpace(wordIndex)" aria-hidden="true">&nbsp;</span>
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import { SentenceTranslationData as TranslationData, TranslationMode, WordTranslation } from '@lingvo-news/contracts'
import { playWordAudio } from '../services/word-audio-service'

interface Props {
  mode?: TranslationMode
  translationData: TranslationData
  showDebugControls?: boolean
  showButtons?: boolean
  translate?: boolean
  transliterate?: boolean
  soundEnabled?: boolean
  primaryTextClass?: string
  secondaryTextClass?: string
}

const props = withDefaults(defineProps<Props>(), {
  showDebugControls: false,
  showButtons: true,
  primaryTextClass: 'primary-content-text',
  transliterate: false,
  soundEnabled: false,
  secondaryTextClass: 'translation-content-text'
})

const emit = defineEmits<{
  (e: 'audio-auth-required'): void
  (e: 'audio-paywall-required'): void
}>()

const activeWordIndices = ref<Set<number>>(new Set())
const wordRefs = ref<Map<number, HTMLElement>>(new Map())
const activeWordWidths = ref<Map<number, number>>(new Map())

const effectiveTranslate = computed(() => props.translate === true)
const primaryTextClass = computed(() => props.primaryTextClass)
const secondaryTextClass = computed(() => props.secondaryTextClass)

const translationsByIndex = computed(() => {
  const map = new Map<number, WordTranslation>()
  for (const item of props.translationData.words) {
    map.set(item.wordIndex, item)
  }
  return map
})

const hasTranslation = (wordIndex: number): boolean => {
  const word = translationsByIndex.value.get(wordIndex)
  return !!word?.translation
}

const isWordActive = (wordIndex: number): boolean => {
  return activeWordIndices.value.has(wordIndex)
}

const getDisplayWord = (wordIndex: number): string => {
  const originalWord = props.translationData.originalWords[wordIndex] || ''
  const word = translationsByIndex.value.get(wordIndex)
  if (!word?.translation) {
    return originalWord
  }

  if (props.transliterate && word.transliteration) {
    return word.transliteration
  }

  return word.translation
}

const getTranslatedWordTextClass = (): string => {
  // Transliteration is same-language script, so it should keep primary font family.
  if (props.transliterate) {
    return primaryTextClass.value
  }
  return secondaryTextClass.value
}

const getOriginalWord = (wordIndex: number): string => {
  return props.translationData.originalWords[wordIndex] || ''
}

const getRenderedWord = (wordIndex: number): string => {
  if (isWordActive(wordIndex)) {
    return getOriginalWord(wordIndex)
  }

  return getDisplayWord(wordIndex)
}

const isClosingPunctuation = (word: string): boolean => /^[,.;:!?%\]\)\}"'»]+$/.test(word)
const isOpeningPunctuation = (word: string): boolean => /^[\[\("'«]+$/.test(word)

const needsTrailingSpace = (wordIndex: number): boolean => {
  if (wordIndex >= props.translationData.originalWords.length - 1) {
    return false
  }

  const current = props.translationData.originalWords[wordIndex] || ''
  const next = props.translationData.originalWords[wordIndex + 1] || ''

  if (isOpeningPunctuation(current)) {
    return false
  }

  if (isClosingPunctuation(next)) {
    return false
  }

  return true
}

const handleWordClick = async (wordIndex: number, event: MouseEvent): Promise<void> => {
  const translation = translationsByIndex.value.get(wordIndex)
  if (!translation?.translation) {
    return
  }

  event.stopPropagation()

  if (isWordActive(wordIndex)) {
    closeWord(wordIndex)
    return
  }

  setActiveWordWidthPolicy(wordIndex)
  const nextActive = new Set(activeWordIndices.value)
  nextActive.add(wordIndex)
  activeWordIndices.value = nextActive

  if (props.soundEnabled && translation.normalizedWordId) {
    const playResult = await playWordAudio(translation.normalizedWordId)
    if (playResult === 'unauthorized') {
      emit('audio-auth-required')
    } else if (playResult === 'paywall') {
      emit('audio-paywall-required')
    }
  }
}

const closeWord = (wordIndex: number): void => {
  const nextActive = new Set(activeWordIndices.value)
  nextActive.delete(wordIndex)
  activeWordIndices.value = nextActive
  activeWordWidths.value.delete(wordIndex)
}

const setWordRef = (wordIndex: number, el: unknown): void => {
  if (el instanceof HTMLElement) {
    wordRefs.value.set(wordIndex, el)
  } else {
    wordRefs.value.delete(wordIndex)
  }
}

const getWordStyle = (wordIndex: number): Record<string, string> => {
  if (!isWordActive(wordIndex)) {
    return {}
  }

  const width = activeWordWidths.value.get(wordIndex)
  if (!width) {
    return {}
  }

  return {
    width: `${width}px`,
    textAlign: 'center'
  }
}

const setActiveWordWidthPolicy = (wordIndex: number): void => {
  const el = wordRefs.value.get(wordIndex)
  if (!el) {
    return
  }

  const displayedWidth = measureTextWidthLikeElement(el, getDisplayWord(wordIndex))
  const originalWidth = measureTextWidthLikeElement(el, getOriginalWord(wordIndex))

  // Keep complexity/rule:
  // if original is wider, allow expansion (no lock);
  // otherwise lock to displayed width to avoid shrink jump.
  if (originalWidth > displayedWidth) {
    activeWordWidths.value.delete(wordIndex)
  } else {
    activeWordWidths.value.set(wordIndex, displayedWidth)
  }
}

const measureTextWidthLikeElement = (referenceEl: HTMLElement, text: string): number => {
  const probe = document.createElement('span')
  const styles = window.getComputedStyle(referenceEl)

  probe.textContent = text
  probe.style.position = 'absolute'
  probe.style.visibility = 'hidden'
  probe.style.whiteSpace = 'pre'
  probe.style.pointerEvents = 'none'
  probe.style.left = '-99999px'
  probe.style.top = '-99999px'
  probe.style.fontFamily = styles.fontFamily
  probe.style.fontSize = styles.fontSize
  probe.style.fontWeight = styles.fontWeight
  probe.style.fontStyle = styles.fontStyle
  probe.style.letterSpacing = styles.letterSpacing
  probe.style.textTransform = styles.textTransform

  document.body.appendChild(probe)
  const width = probe.getBoundingClientRect().width
  probe.remove()
  return width
}

watch(
  () => props.transliterate,
  async () => {
    await nextTick()

    const recalculated = new Map<number, number>()
    for (const wordIndex of activeWordIndices.value) {
      const el = wordRefs.value.get(wordIndex)
      if (!el) {
        continue
      }

      const displayedWidth = measureTextWidthLikeElement(el, getDisplayWord(wordIndex))
      const originalWidth = measureTextWidthLikeElement(el, getOriginalWord(wordIndex))
      if (originalWidth <= displayedWidth) {
        recalculated.set(wordIndex, displayedWidth)
      }
    }

    activeWordWidths.value = recalculated
  }
)

watch(
  () => props.translationData,
  () => {
    activeWordIndices.value = new Set()
    activeWordWidths.value = new Map()
    wordRefs.value = new Map()
  }
)

</script>

<style scoped lang="less">
@import "../styles/text-styles.less";

.translated-text-v2 {
  width: 100%;
  text-align: justify;
}
.title .translated-text-v2 {
  text-align: center;
}

.text-content,
.full-translation-content {
  //line-height: @text-line-height;
  font-size: @text-font-size;
  text-rendering: optimizeLegibility;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  white-space: normal;
}

.word-wrap {
  position: relative;
  display: inline;
}

.word {
  display: inline-block;
}

.clickable-word {
  cursor: pointer;
}

.translated-word {
  color: #1a73e8;
}

.replacement-word {
  color: #1a73e8;
}
</style>
